// @avantia common-code
import { SMap, rangeWorker } from '@avantia/client-and-server-utilities';
import { formatDate } from '@avantia/form-validation';
import { HpError, hasOwnProperty } from '@avantia/logging-and-errors';
import {
  HpAddControlGroupActionProps,
  HpAnswerMap,
  HpCommonSection,
  createControlId,
  getIdentifierConfigDefaultValues,
  identifierConfigChanged,
  setCustomerAnswer
} from '@avantia/questionset-model';
import { createAddress } from '../createAddress';
import { getAnswersFromAnswerMap, setMetaDataInAnswers } from '../engineClient/tools/clientAndServerUtilities';
import { HpClientButtonData, HpClientQuestion, HpClientQuestionData } from '../engineClient/types';
import { CustomerDetails, Excesses, Policy, PolicyInstancesCustomData } from '../types/policyTypes';
import { HpClaimsMetaData } from '../types/stateTypes';
import { questionIds } from '../types/questionConstants';

let { maxGroupsAllowed } = getIdentifierConfigDefaultValues();

identifierConfigChanged(({ maxGroupsAllowed: groups }) => {
  maxGroupsAllowed = groups;
});

export interface ApplyPolicyAnswersProps {
  policy: Policy;
  answers: HpAnswerMap;
  questions: SMap<HpClientQuestion>;
  sections: SMap<HpCommonSection>;
  productId: string;
}

/** Writes the policy's answers into `answers`. */
export function applyPolicyAnswers({ policy, answers, questions, sections, productId }: ApplyPolicyAnswersProps): void {
  const {
    productDescription = 'Evan',
    specifiedItems,
    accidentalDamageLevel,
    policyDetailsId,
    historyId,
    policyNumber,
    title,
    forename,
    surname,
    dateOfBirth,
    phoneNumber,
    altPhoneNumber,
    startDate,
    locality,
    street,
    house,
    postcode,
    county,
    endDate,
    contentsSumInsured,
    buildingsSumInsured,
    coverType: policyCoverType,
    isFloodRe,
    isUnoccupied,
    hasUnoccupiedExtendedPerils,
    inceptionDate
  } = policy;
  const { policyInstance: policyInstanceQid } = questionIds;
  const policyInstancesQuestion = questions[policyInstanceQid];
  const { data } = policyInstancesQuestion;
  const { customData } = data as HpClientQuestionData;
  const { productNamesMap } = customData as unknown as PolicyInstancesCustomData;
  if (!productNamesMap) {
    throw new HpError(`Policy instances question must contain a productNames map in the custom data field.`);
  }

  const questionSetId = productNamesMap[productDescription.toUpperCase()];
  if (!questionSetId) {
    throw new HpError(`The product names map does not contain a questionSetId for product ${productDescription}`);
  }

  const { coverType, customerDetails } = getAnswersFromAnswerMap({
    productId,
    answers,
    keys: ['coverType', 'customerDetails'],
    options: { returnAnyAnswerType: true }
  });
  const adCoverLevelAnswer = coverType ? accidentalDamageLevel[coverType] : 'NONE';
  const { emailAddress } = JSON.parse(customerDetails) as CustomerDetails;
  const {
    hasSpecifiedItems: hasSpecifiedItemsQid,
    adCoverLevel: adCoverLevelQid,
    questionSetId: questionSetIdQid,
    coverType: coverTypeQid,
    policyCoverType: policyCoverTypeQid,
    specifiedItemCoverLevel,
    specifiedItems: specifiedItemsQid,
    title: titleQid,
    forename: forenameQid,
    surname: surnameQid,
    dateOfBirth: dateOfBirthQid,
    phoneNumber: phoneNumberQid,
    altPhoneNumber: altPhoneNumberQid,
    historyId: historyIdQid,
    policyDetailsId: policyDetailsIdQid,
    startDate: startDateQid,
    endDate: endDateQid,
    secondaryEmail: secondaryEmailQid,
    specifiedItemsCount: specifiedItemsCountQid,
    emailAddress: emailAddressQid,
    policyholderFullName: policyholderFullNameQid,
    riskPostcode: postcodeQid,
    riskAddress: riskAddressQid,
    accidentalDamageLevelBuildings: accidentalDamageLevelBuildingsQid,
    accidentalDamageLevelContents: accidentalDamageLevelContentsQid,
    contentsSumInsured: contentsSumInsuredQid,
    buildingsSumInsured: buildingsSumInsuredQid,
    policyBooklet: policyBookletQid,
    isFloodRe: isFloodReQid,
    isUnoccupied: isUnoccupiedQid,
    hasUnoccupiedExtendedPerils: hasUnoccupiedExtendedPerilsQid,
    product: productQid,
    inceptionDate: inceptionDateQid
  } = questionIds;
  const hasSpecifiedItems = !!(specifiedItems || []).length;
  // TODO: Eventually move this to the question set. Change mappingField to a map of system to mapping field.
  // Example: mappingField: { TCAS: "hasSpecifiedItems", SV: "has specified items" }
  setCustomerAnswer(answers, hasSpecifiedItemsQid, hasSpecifiedItems);
  setCustomerAnswer(answers, specifiedItemsCountQid, hasSpecifiedItems ? specifiedItems.length : 0);
  setCustomerAnswer(answers, adCoverLevelQid, adCoverLevelAnswer);
  setCustomerAnswer(answers, accidentalDamageLevelBuildingsQid, accidentalDamageLevel.Buildings);
  setCustomerAnswer(answers, accidentalDamageLevelContentsQid, accidentalDamageLevel.Contents);
  setCustomerAnswer(answers, questionSetIdQid, questionSetId);
  setCustomerAnswer(answers, coverTypeQid, null);
  setCustomerAnswer(answers, policyCoverTypeQid, policyCoverType);
  setCustomerAnswer(answers, specifiedItemsQid, null);
  setCustomerAnswer(answers, specifiedItemCoverLevel, hasSpecifiedItems ? null : 'NONE');
  setCustomerAnswer(answers, titleQid, title);
  setCustomerAnswer(answers, forenameQid, forename);
  setCustomerAnswer(answers, surnameQid, surname);
  setCustomerAnswer(answers, policyholderFullNameQid, `${title} ${forename} ${surname}`);
  setCustomerAnswer(answers, dateOfBirthQid, dateOfBirth.toLocaleString('en-GB'));
  setCustomerAnswer(answers, phoneNumberQid, phoneNumber);
  setCustomerAnswer(answers, altPhoneNumberQid, altPhoneNumber);
  setCustomerAnswer(answers, secondaryEmailQid, emailAddress);
  setCustomerAnswer(answers, historyIdQid, historyId.toString());
  setCustomerAnswer(answers, policyDetailsIdQid, policyDetailsId);
  setCustomerAnswer(answers, startDateQid, startDate);
  setCustomerAnswer(answers, endDateQid, endDate);
  setCustomerAnswer(answers, inceptionDateQid, inceptionDate);
  setCustomerAnswer(answers, emailAddressQid, emailAddress);
  setExcessesAnswers(answers, policy.excesses);
  setCustomerAnswer(answers, postcodeQid, postcode);
  setCustomerAnswer(answers, riskAddressQid, createAddress({ county, house, locality, postcode, street }));
  setCustomerAnswer(answers, contentsSumInsuredQid, contentsSumInsured.toString());
  setCustomerAnswer(answers, buildingsSumInsuredQid, buildingsSumInsured.toString());
  setCustomerAnswer(answers, isFloodReQid, isFloodRe);
  setCustomerAnswer(answers, isUnoccupiedQid, isUnoccupied);
  setCustomerAnswer(answers, hasUnoccupiedExtendedPerilsQid, hasUnoccupiedExtendedPerils);
  setCustomerAnswer(answers, productQid, productDescription);
  setRepeatingGroupsAnswers({ policy, answers, questions, sections, productId });

  // TODO - Move Policy Booklet base url to config.
  setCustomerAnswer(
    answers,
    policyBookletQid,
    `https://www.homeprotect.co.uk/images/Policy-booklets/Homeprotect_booklet_${productDescription}.pdf`
  );

  setMetaDataInAnswers({
    productId,
    answers,
    metadata: {
      policyDetailsId,
      historyId,
      policyNumber
    } as HpClaimsMetaData
  });
}

export function setExcessesAnswers(answers: HpAnswerMap, { compulsoryExcesses, voluntaryExcesses }: Excesses): void {
  const excesses = [...compulsoryExcesses, ...voluntaryExcesses];
  setCustomerAnswer(answers, questionIds.excesses, excesses as any[]);
}

// TODO - This probably shouldn't be repeating groups, but arrays...
// endorsements: { code: code, name: name, description: endorsement description }
export function setRepeatingGroupsAnswers({ policy, answers, questions, sections }: ApplyPolicyAnswersProps): void {
  const { endorsements, addonDetails, additionalPolicyHolders, claims } = policy;
  const { addons } = addonDetails;
  const {
    endorsementDetails,
    endorsementCode,
    endorsementHeader,
    endorsementDescription,
    addonDetails: addonDetailsQid,
    addonDescription,
    addonCode,
    jphDetails,
    jphDob,
    jphName,
    previousClaimDetails,
    previousClaimCauseCode,
    previousClaimCost,
    previousClaimDate
  } = questionIds;
  if (endorsements.length) {
    setCustomerAnswer(answers, endorsementDetails, true);
    endorsements.forEach((endorsement, index) => {
      const { description, code, name } = endorsement;
      const groupNumber = index + 1;
      setAnswer(endorsementCode, groupNumber, code);
      setAnswer(endorsementHeader, groupNumber, name);
      setAnswer(endorsementDescription, groupNumber, description.replace(/(<([^>]+)>)/gi, ''));
    });
  } else {
    removeGroupAnswers({ answers, questions, sections, parentQid: endorsementDetails });
  }

  if (addons.length) {
    setCustomerAnswer(answers, addonDetailsQid, true);
    addons.forEach((addon, index) => {
      const { code, name } = addon;
      const groupNumber = index + 1;
      setAnswer(addonCode, groupNumber, code);
      setAnswer(addonDescription, groupNumber, name);
    });
  } else {
    removeGroupAnswers({ answers, questions, sections, parentQid: addonDetailsQid });
  }

  if (additionalPolicyHolders.length) {
    setCustomerAnswer(answers, jphDetails, true);
    additionalPolicyHolders.forEach((jph, index) => {
      const { forename, surname, dateOfBirth, title } = jph;
      const groupNumber = index + 1;
      setAnswer(jphDob, groupNumber, dateOfBirth);
      setAnswer(jphName, groupNumber, `${title} ${forename} ${surname}`);
    });
  } else {
    removeGroupAnswers({ answers, questions, sections, parentQid: jphDetails });
  }

  if (claims.length) {
    setCustomerAnswer(answers, previousClaimDetails, true);
    claims.forEach((claim, index) => {
      const { claimDate, claimSection, claimAmount } = claim;
      const groupNumber = index + 1;
      setAnswer(previousClaimDate, groupNumber, formatDate(new Date(claimDate)));
      setAnswer(previousClaimCauseCode, groupNumber, claimSection);
      setAnswer(previousClaimCost, groupNumber, claimAmount.toString());
    });
  } else {
    removeGroupAnswers({ answers, questions, sections, parentQid: previousClaimDetails });
  }

  function setAnswer(questionId: string, groupNumber: number, value: any) {
    setCustomerAnswer(answers, createControlId({ questionId, groupNumber, options: { noPrefix: true } }), value);
  }
}

interface RemoveGroupAnswersProps {
  answers: HpAnswerMap;
  questions: SMap<HpClientQuestion>;
  sections: SMap<HpCommonSection>;
  parentQid: string;
}

interface FindGroupQuestionIdsProps {
  questions: SMap<HpClientQuestion>;
  sections: SMap<HpCommonSection>;
  groupId: string;
}

/**
 * The questions a repeating group holds. The client's own sections list them; a question set's sections do not, so
 * there they are found from the questions that name the group. Only used to delete answers, so order does not matter.
 */
function findGroupQuestionIds({ questions, sections, groupId }: FindGroupQuestionIdsProps): string[] {
  const { questionIds: questionIdsInSection } = sections[groupId] || {};
  if (questionIdsInSection) {
    return questionIdsInSection;
  }

  return Object.values(questions)
    .filter((question) => question.groupId === groupId)
    .map((question) => question.questionId);
}

// This does not update the displayState. It only deletes the group answers.
function removeGroupAnswers({ answers, questions, sections, parentQid }: RemoveGroupAnswersProps): void {
  rangeWorker(1, maxGroupsAllowed, (groupNumber) => {
    const question = questions[parentQid];
    const buttonData = question.data as HpClientButtonData;
    const { dependentActions } = buttonData;
    let groupId: string | null = null;
    if (!dependentActions) {
      throw new HpError(`parent questionId ${parentQid} does not have any dependent actions`);
    }

    for (const action of dependentActions) {
      const isAddControlGroup = action.action === 'add-control-group';
      if (isAddControlGroup) {
        const { props } = action;
        ({ groupId } = props as HpAddControlGroupActionProps);
        break;
      }
    }

    if (!groupId) {
      throw new HpError(`parent question id ${parentQid} does not have an "add-control-group" action.`);
    }

    const questionIdsInGroup = findGroupQuestionIds({ questions, sections, groupId });
    setCustomerAnswer(answers, parentQid, false);
    for (const id of questionIdsInGroup) {
      const controlId = createControlId({
        groupNumber,
        questionId: id,
        options: { noPrefix: true }
      });
      if (!hasOwnProperty(answers, controlId)) {
        return false;
      }

      delete answers[controlId];
    }

    return true;
  });
}
